import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ChefHat, LogOut, Loader2, Clock, Flame, Fish, Utensils, Droplets, ShieldCheck, ShieldAlert, ClipboardList, Store, SprayCan, Sunrise, Moon, PenLine, CalendarDays, CheckCheck, CalendarX, Send, Mail, X } from "lucide-react";
import ChecklistItem from "@/components/ChecklistItem";
import ScheduleTimeline from "@/components/ScheduleTimeline";
import CrewSignoff from "@/components/CrewSignoff";
import FinalizePanel from "@/components/FinalizePanel";
import ProgressRing from "@/components/ProgressRing";
import ScheduleManager from "@/components/ScheduleManager";
import NotificationBell from "@/components/NotificationBell";

const SECTION_ICON = {
  schedule: Clock, grill: Flame, fry: Fish, expo: Utensils, dish: Droplets, verify: ShieldCheck,
  mod: ClipboardList, cook1: Flame, cook2: Fish, foh: Store, foh_tasks: Store, restroom: SprayCan,
};

export default function StaffApp() {
  const { user, logout, refreshUser } = useAuth();
  const [checklists, setChecklists] = useState([]);
  const [activeCid, setActiveCid] = useState(null);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingCl, setLoadingCl] = useState(false);
  const [roster, setRoster] = useState([]);
  const [showSchedule, setShowSchedule] = useState(false);
  const [doneBusy, setDoneBusy] = useState(false);
  const [myLocations, setMyLocations] = useState([]);
  const [activeLoc, setActiveLoc] = useState(localStorage.getItem("pb_location") || "");
  const [closers, setClosers] = useState([]);
  const [onDuty, setOnDuty] = useState(null);
  const [showPrefs, setShowPrefs] = useState(false);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/locations"); setMyLocations(data.locations || []); } catch (e) { console.error("Failed to load locations", e); } })();
  }, []);

  useEffect(() => {
    (async () => { try { const { data } = await api.get("/on-duty-supervisors"); setClosers(data.supervisors || []); } catch (e) { console.error("Failed to load on-duty supervisors", e); } })();
  }, []);

  const loadOnDuty = useCallback(async () => {
    if (user.role !== "supervisor") return;
    try { const { data } = await api.get("/on-duty"); setOnDuty(data); }
    catch (e) { console.error("Failed to load on-duty status", e); }
  }, [user.role]);
  useEffect(() => { loadOnDuty(); }, [loadOnDuty]);

  const switchLoc = (id) => { setActiveLoc(id); localStorage.setItem("pb_location", id); window.location.reload(); };

  const isSupervisor = user.role === "supervisor" || user.role === "owner";
  const activeShift = checklists.find((c) => c.id === activeCid)?.shift;
  const activeSlot = activeShift === "opening" ? "day" : activeShift === "closing" ? "night" : "any";
  const canManage = user.role === "owner" || (user.role === "supervisor" && (
    activeSlot === "any" ? (!!onDuty?.day?.me || !!onDuty?.night?.me)
      : activeSlot === "day" ? !!onDuty?.day?.me : !!onDuty?.night?.me));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/checklists");
        setChecklists(data.checklists);
        setActiveCid(data.checklists[0]?.id || null);
      } catch (e) {
        toast.error(apiErr(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadChecklist = useCallback(async (cid) => {
    if (!cid) return;
    setLoadingCl(true);
    try {
      const { data } = await api.get(`/checklists/${cid}/active`);
      setData(data);
      setTab(data.sections[0]?.id || "signoff");
      try {
        const r = await api.get(`/schedule?checklist_id=${cid}`);
        setRoster((r.data.assignments || []).map((a) => a.name));
      } catch (e) { console.error("Failed to load roster", e); setRoster([]); }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoadingCl(false);
    }
  }, []);

  useEffect(() => { loadChecklist(activeCid); }, [activeCid, loadChecklist]);

  const disabled = data?.session?.status === "completed";

  const applyResp = (resp) => setData((prev) => ({ ...prev, session: resp.session, progress: resp.progress }));

  const setCorrective = async (item_key, note) => {
    try {
      const { data: resp } = await api.post(`/checklists/${activeCid}/corrective`, { item_key, note });
      applyResp(resp);
      toast.success("Corrective action saved");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggle = async (item_key, checked, value) => {
    setData((prev) => {
      const c = { ...(prev.session.completions || {}) };
      if (checked) c[item_key] = { checked: true, checked_by: user.name, checked_at: new Date().toISOString(), ...(value ? { value } : {}) };
      else delete c[item_key];
      return { ...prev, session: { ...prev.session, completions: c } };
    });
    try {
      const { data: resp } = await api.post(`/checklists/${activeCid}/toggle`, { item_key, checked, value });
      applyResp(resp);
    } catch (e) { toast.error(apiErr(e)); loadChecklist(activeCid); }
  };

  const uploadPhoto = async (item_key, file) => {
    const fd = new FormData();
    fd.append("item_key", item_key);
    fd.append("file", file);
    try {
      const { data: resp } = await api.post(`/checklists/${activeCid}/photo`, fd);
      applyResp(resp);
      toast.success("Photo added");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const removePhoto = async (item_key) => {
    try {
      const { data: resp } = await api.delete(`/checklists/${activeCid}/photo?item_key=${encodeURIComponent(item_key)}`);
      applyResp(resp);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const bulkSection = async (section_id, checked) => {
    try {
      const { data: resp } = await api.post(`/checklists/${activeCid}/bulk`, { section_id, checked });
      applyResp(resp);
      toast.success(checked ? "Section checked off" : "Section cleared");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const notifyDone = async () => {
    setDoneBusy(true);
    try {
      await api.post(`/checklists/${activeCid}/notify-done`);
      toast.success("Your manager has been notified you're done");
    } catch (e) { toast.error(apiErr(e)); } finally { setDoneBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0E0E0E]"><Loader2 className="h-8 w-8 animate-spin text-[#E11D2A]" /></div>;
  }

  if (checklists.length === 0) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] relative z-10 flex flex-col">
        <header className="border-b border-[#2E2E2E] bg-[#0E0E0E]/90 backdrop-blur-md">
          <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-lg bg-[#E11D2A] flex items-center justify-center"><ChefHat className="h-5 w-5 text-white" /></div>
              <div><p className="font-heading font-bold leading-none">Paige's Bistro</p><p className="text-[11px] text-[#6B6B6B] mt-0.5 capitalize">{user.name}</p></div>
            </div>
            <div className="flex items-center gap-2">
              {isSupervisor && (
                <button onClick={() => setShowSchedule(true)} data-testid="manage-schedule-button" className="inline-flex items-center gap-2 rounded-lg border border-[#2E2E2E] bg-[#171717] px-3 py-2 text-sm hover:border-[#E11D2A]/60 transition-colors">
                  <CalendarDays className="h-4 w-4 text-[#E11D2A]" /> Assign crew
                </button>
              )}
              {myLocations.length > 1 && (
                <select data-testid="staff-location-switcher" value={activeLoc} onChange={(e) => switchLoc(e.target.value)}
                  className="h-9 rounded-lg border border-[#2E2E2E] bg-[#171717] px-2 text-xs outline-none focus:border-[#E11D2A] max-w-[130px]">
                  {myLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              <button onClick={() => setShowPrefs(true)} data-testid="staff-prefs-button" className="h-9 w-9 rounded-lg border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors" title="Reminders"><Mail className="h-4 w-4 text-[#A3A3A3]" /></button>
              <NotificationBell />
              <button onClick={logout} data-testid="logout-button" className="h-9 w-9 rounded-lg border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors"><LogOut className="h-4 w-4 text-[#A3A3A3]" /></button>
            </div>
          </div>
        </header>
        <ClosersStrip closers={closers} />
        {user.role === "supervisor" && <OnDutyBar onDuty={onDuty} onChanged={loadOnDuty} />}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="no-assignment">
          <CalendarX className="h-12 w-12 text-[#6B6B6B] mb-4" />
          <h2 className="font-heading text-2xl font-bold">Nothing assigned yet</h2>
          <p className="text-[#A3A3A3] mt-2 max-w-sm">You're not scheduled for a shift today. Send a request and your supervisor will assign you a section.</p>
          {!isSupervisor && <RequestAssignmentButton />}
        </div>
        {showSchedule && <ScheduleManager onClose={() => setShowSchedule(false)} onSaved={() => window.location.reload()} />}
        {showPrefs && <PreferencesModal user={user} onClose={() => setShowPrefs(false)} onSaved={refreshUser} />}
      </div>
    );
  }

  const completions = data?.session?.completions || {};
  const photos = data?.session?.photos || {};
  const progress = data?.progress || {};
  const sectionProg = Object.fromEntries((progress.sections || []).map((s) => [s.id, s]));

  const tabs = data ? [
    ...data.sections.map((s) => ({ id: s.id, label: s.title, icon: SECTION_ICON[s.id] || ClipboardList })),
    { id: "signoff", label: "Sign-Off", icon: PenLine },
    ...(isSupervisor ? [{ id: "finalize", label: "Finalize", icon: ShieldCheck }] : []),
  ] : [];

  const currentSection = data?.sections.find((s) => s.id === tab);

  return (
    <div className="min-h-screen bg-[#0E0E0E] relative z-10">
      {/* Header */}
      <header className="sticky top-0 z-20 border-b border-[#2E2E2E] bg-[#0E0E0E]/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[#E11D2A] flex items-center justify-center">
              <ChefHat className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-heading font-bold leading-none flex items-center gap-2">
                Paige's Bistro
                {isSupervisor && <RoleBadge role={user.role} />}
              </p>
              <p className="text-[11px] text-[#6B6B6B] mt-0.5 capitalize">{user.name} · {user.area === "all" ? user.role : `${user.area} · ${user.role}`}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {myLocations.length > 1 && (
              <select data-testid="staff-location-switcher" value={activeLoc} onChange={(e) => switchLoc(e.target.value)}
                className="h-9 rounded-lg border border-[#2E2E2E] bg-[#171717] px-2 text-xs outline-none focus:border-[#E11D2A] max-w-[130px]">
                {myLocations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            {!isSupervisor && data && (
              <button onClick={notifyDone} disabled={doneBusy} data-testid="notify-done-button" className="inline-flex items-center gap-1.5 rounded-lg bg-[#E11D2A] px-3 py-2 text-xs font-semibold text-white hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
                <CheckCheck className="h-4 w-4" /> {doneBusy ? "…" : "I'm done"}
              </button>
            )}
            <button onClick={() => setShowPrefs(true)} data-testid="staff-prefs-button" className="h-9 w-9 rounded-lg border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors" title="Reminders"><Mail className="h-4 w-4 text-[#A3A3A3]" /></button>
            <NotificationBell />
            <button onClick={logout} data-testid="logout-button" className="h-9 w-9 rounded-lg border border-[#2E2E2E] bg-[#171717] flex items-center justify-center hover:border-[#E11D2A]/60 transition-colors">
              <LogOut className="h-4 w-4 text-[#A3A3A3]" />
            </button>
          </div>
        </div>
        {isSupervisor && (
          <div className="max-w-3xl mx-auto px-4 pb-2 flex justify-end">
            <button onClick={() => setShowSchedule(true)} data-testid="manage-schedule-button" className="inline-flex items-center gap-2 rounded-lg border border-[#2E2E2E] bg-[#171717] px-3 py-1.5 text-xs font-medium hover:border-[#E11D2A]/60 transition-colors">
              <CalendarDays className="h-3.5 w-3.5 text-[#E11D2A]" /> Assign today's crew
            </button>
          </div>
        )}

        {/* Checklist selector */}
        <div className="max-w-3xl mx-auto px-3 pb-2 flex flex-wrap gap-2">
          {checklists.map((c) => {
            const active = c.id === activeCid;
            const Icon = c.shift === "opening" ? Sunrise : Moon;
            return (
              <button
                key={c.id}
                data-testid={`checklist-select-${c.id}`}
                onClick={() => setActiveCid(c.id)}
                className={`shrink-0 flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold border transition-all
                  ${active ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#171717] border-[#2E2E2E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}
              >
                <Icon className="h-4 w-4" /> {c.title}
              </button>
            );
          })}
        </div>

        {/* Overall bar */}
        {data && (
          <div className="max-w-3xl mx-auto px-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-[#232323] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#E11D2A] to-[#EF4444] transition-all duration-500" style={{ width: `${progress.overall ? (progress.overall.done / progress.overall.total) * 100 : 0}%` }} />
              </div>
              <span className="font-mono text-xs text-[#A3A3A3]">{progress.overall?.done}/{progress.overall?.total}</span>
            </div>
          </div>
        )}
        <ClosersStrip closers={closers} />
        {user.role === "supervisor" && <OnDutyBar onDuty={onDuty} onChanged={loadOnDuty} />}
      </header>

      {/* Section tabs */}
      {data && (
        <div className="sticky top-[133px] z-10 bg-[#0E0E0E]/90 backdrop-blur-md border-b border-[#242424]">
          <div className="max-w-3xl mx-auto px-2 flex flex-wrap gap-1 py-2">
            {tabs.map((t) => {
              const Icon = t.icon;
              const p = sectionProg[t.id];
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`section-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`shrink-0 flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors
                    ${active ? "bg-[#E11D2A] text-white" : "text-[#A3A3A3] hover:bg-[#171717]"}`}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                  {p && <span className={`font-mono text-[11px] ${active ? "text-white/70" : p.done === p.total ? "text-[#16A34A]" : "text-[#6B6B6B]"}`}>{p.done}/{p.total}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-24 fade-up" key={`${activeCid}-${tab}`}>
        {loadingCl || !data ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#E11D2A]" /></div>
        ) : (
          <>
            {disabled && (
              <div className="mb-4 rounded-xl border border-[#16A34A]/40 bg-[#16A34A]/10 px-4 py-3 text-sm text-[#16A34A]">
                This {data.checklist.title} has been verified & submitted. It is now locked.
              </div>
            )}

            {currentSection && currentSection.kind === "timeline" && (
              <>
                <SectionHead icon={Clock} title={currentSection.title} sub="8:30 PM – 10:55 PM" ring={sectionProg[tab]} />
                <ScheduleTimeline items={currentSection.items} completions={completions} disabled={disabled} onToggle={toggle} />
              </>
            )}

            {currentSection && currentSection.kind === "checklist" && (
              <>
                <SectionHead icon={SECTION_ICON[tab] || ClipboardList} title={currentSection.title} sub="Tap 📷 to add photo proof" ring={sectionProg[tab]} />
                {canManage && !disabled && (
                  <div className="flex justify-end mb-3">
                    <button
                      data-testid="bulk-check-button"
                      onClick={() => bulkSection(tab, (sectionProg[tab]?.done || 0) < (sectionProg[tab]?.total || 0))}
                      className="inline-flex items-center gap-2 rounded-lg border border-[#2E2E2E] bg-[#171717] px-3.5 py-2 text-sm font-medium text-[#F5F5F5] hover:border-[#E11D2A]/60 transition-colors"
                    >
                      <CheckCheck className="h-4 w-4 text-[#E11D2A]" />
                      {(sectionProg[tab]?.done || 0) < (sectionProg[tab]?.total || 0) ? "Check all in this area" : "Clear all"}
                    </button>
                  </div>
                )}
                <div className="space-y-2.5" data-testid={`section-panel-${tab}`}>
                  {currentSection.items.map((it) => (
                    <ChecklistItem
                      key={it.key}
                      item={it}
                      meta={completions[it.key]}
                      photo={photos[it.key]}
                      disabled={disabled}
                      allowPhoto
                      onToggle={(v, val) => toggle(it.key, v, val)}
                      onPhoto={(f) => uploadPhoto(it.key, f)}
                      onRemovePhoto={() => removePhoto(it.key)}
                      onCorrective={(note) => setCorrective(it.key, note)}
                    />
                  ))}
                </div>
              </>
            )}

            {tab === "signoff" && (
              <>
                <SectionHead icon={PenLine} title="Crew Sign-Off" sub={data.checklist.title} />
                <CrewSignoff cid={activeCid} roles={data.checklist.signoff_roles} session={data.session} defaultName={user.name} suggestions={roster} onSaved={(s) => setData((prev) => ({ ...prev, session: s }))} />
              </>
            )}

            {tab === "finalize" && (
              <FinalizePanel
                cid={activeCid}
                checklistTitle={data.checklist.title}
                session={data.session}
                progress={progress}
                canFinalize={canManage}
                onCompleted={applyResp}
              />
            )}
          </>
        )}
      </main>
      {showSchedule && <ScheduleManager onClose={() => setShowSchedule(false)} onSaved={() => { setShowSchedule(false); loadChecklist(activeCid); }} />}
      {showPrefs && <PreferencesModal user={user} onClose={() => setShowPrefs(false)} onSaved={refreshUser} />}
    </div>
  );
}

function SectionHead({ icon: Icon, title, sub, ring }) {  return (
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-[#232323] border border-[#2E2E2E] flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#E11D2A]" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-bold leading-none">{title}</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">{sub}</p>
        </div>
      </div>
      {ring && <ProgressRing done={ring.done} total={ring.total} />}
    </div>
  );
}

function RoleBadge({ role }) {
  const isOwner = role === "owner";
  return (
    <span data-testid={`role-badge-${role}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${isOwner ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-[#E11D2A]/15 border-[#E11D2A]/40 text-[#EF4444]"}`}>
      <ShieldCheck className="h-3 w-3" /> {isOwner ? "Owner" : "Closer"}
    </span>
  );
}

function ClosersStrip({ closers }) {
  if (!closers || closers.length === 0) return null;
  return (
    <div className="border-t border-[#242424] bg-[#0E0E0E]/80" data-testid="closers-on-duty">
      <div className="max-w-3xl mx-auto px-4 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <span className="shrink-0 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#6B6B6B]">
          <ShieldCheck className="h-3.5 w-3.5 text-[#E11D2A]" /> Can close out
        </span>
        <div className="flex items-center gap-1.5">
          {closers.map((c) => (
            <span key={c.name} data-testid={`closer-chip-${c.name}`} className="shrink-0 inline-flex items-center gap-1 rounded-full border border-[#E11D2A]/40 bg-[#E11D2A]/10 px-2.5 py-1 text-xs font-semibold text-[#F5F5F5]">
              {c.name}
              {c.role === "owner" && <span className="text-[9px] font-bold uppercase text-[#EF4444]">Owner</span>}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function OnDutyBar({ onDuty, onChanged }) {
  const [busy, setBusy] = useState("");
  if (!onDuty) return null;
  const request = async (shift) => {
    setBusy(shift);
    try { await api.post("/on-duty/request", { shift }); toast.success(`${shift === "day" ? "Day" : "Night"} on-duty request sent to the owner`); onChanged?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const Slot = ({ label, shift, slot, Icon }) => (
    <div data-testid={`on-duty-slot-${shift}`} className="flex items-center gap-2 text-xs">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${slot?.me ? "text-[#16A34A]" : "text-[#6B6B6B]"}`} />
      <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B]">{label}</span>
      {slot?.current
        ? <span className={`font-semibold ${slot.me ? "text-[#16A34A]" : "text-[#F5F5F5]"}`}>{slot.current.name}{slot.me ? " · you" : ""}</span>
        : <span className="text-[#A3A3A3]">unassigned</span>}
      {!slot?.me && (slot?.pending
        ? <span data-testid={`on-duty-pending-${shift}`} className="text-[11px] font-semibold text-[#E5A100]">pending…</span>
        : <button data-testid={`request-on-duty-${shift}`} onClick={() => request(shift)} disabled={busy === shift}
            className="inline-flex items-center gap-1 rounded-md bg-[#E11D2A] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
            <Send className="h-3 w-3" /> {busy === shift ? "…" : "Request"}
          </button>)}
    </div>
  );
  return (
    <div data-testid="on-duty-bar" className="border-t border-[#242424] bg-[#0E0E0E]/80">
      <div className="max-w-3xl mx-auto px-4 py-2 flex flex-wrap items-center gap-x-5 gap-y-1.5">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B]">
          <ShieldCheck className="h-3.5 w-3.5 text-[#E11D2A]" /> On duty
        </span>
        <Slot label="Day" shift="day" slot={onDuty.day} Icon={Sunrise} />
        <Slot label="Night" shift="night" slot={onDuty.night} Icon={Moon} />
      </div>
    </div>
  );
}

function PreferencesModal({ user, onClose, onSaved }) {
  const [email, setEmail] = useState(user.email || "");
  const [optIn, setOptIn] = useState(!!user.reminder_opt_in);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (optIn && !email.trim()) return toast.error("Add an email to receive reminders");
    setBusy(true);
    try {
      await api.put("/me/preferences", { email: email.trim(), reminder_opt_in: optIn });
      await onSaved?.();
      toast.success("Reminder settings saved");
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-[#2E2E2E] bg-[#171717]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2E2E2E]">
          <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Mail className="h-5 w-5 text-[#E11D2A]" /> Shift Reminders</h3>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-[#232323] flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-[#6B6B6B] mb-1.5">Your email</label>
            <input data-testid="prefs-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2.5 text-sm outline-none focus:border-[#E11D2A]" />
          </div>
          <button type="button" data-testid="prefs-optin-toggle" onClick={() => setOptIn((v) => !v)}
            className="w-full flex items-center justify-between rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] px-4 py-3 hover:border-[#E11D2A]/50 transition-colors">
            <span className="text-left">
              <span className="block text-sm font-medium">Email me when I'm scheduled</span>
              <span className="block text-[11px] text-[#6B6B6B] mt-0.5">Get a reminder with your sections whenever a supervisor assigns you.</span>
            </span>
            <span className={`ml-3 shrink-0 h-6 w-11 rounded-full p-0.5 transition-colors ${optIn ? "bg-[#E11D2A]" : "bg-[#3a3a3a]"}`}>
              <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${optIn ? "translate-x-5" : ""}`} />
            </span>
          </button>
          <button data-testid="prefs-save-button" onClick={save} disabled={busy}
            className="w-full rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-3 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestAssignmentButton() {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/assignment-request", {});
      setSent(true);
      toast.success("Request sent to your supervisor");
    } catch (e) {
      const msg = apiErr(e);
      if (msg.toLowerCase().includes("already")) setSent(true);
      toast.error(msg);
    } finally { setBusy(false); }
  };
  if (sent) {
    return <p data-testid="request-assignment-sent" className="mt-5 rounded-xl border border-[#16A34A]/40 bg-[#16A34A]/10 px-4 py-3 text-sm text-[#16A34A]">Request sent — your supervisor has been notified.</p>;
  }
  return (
    <button data-testid="request-assignment-button" onClick={send} disabled={busy}
      className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#E11D2A] text-white font-heading font-bold px-5 py-3 hover:bg-[#B91C1C] disabled:opacity-60 transition-colors">
      <Send className="h-4 w-4" /> {busy ? "Sending…" : "Request assignment"}
    </button>
  );
}
